import { Injectable, Optional, SkipSelf } from '@angular/core';
import { Router } from '@angular/router';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, Subject, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';

import { Environment_Service } from "../Environment_Service";
import { LocalStorage_Service } from "../system/LocalStorage_Service";

import { token_login } from "./../../objects/identity/token_login";
import { tms_internal_user_info } from '../../objects/System/user_info/tms_internal_user_info';
import { error_message } from "./../../objects/identity/error_message";

import { v1_3_identity_request } from "../../objects/identity/v1_3_identity_request";
import { v1_3_identity_claim_options } from "../../objects/identity/claims/v1_3_identity_claim_options";

import { v1_3_identity_switch_certificate_request } from "../../objects/identity/switch_certificate/v1_3_identity_switch_certificate_request";
import { v1_3_identity_switch_certificate_response } from "../../objects/identity/switch_certificate/v1_3_identity_switch_certificate_response";

import { Response_Service } from "../Response_Service";
import { Call_Info_Service } from "../Call_Info_Service";


@Injectable({
  providedIn: 'root'
})
export class OAuth_Service {

  constructor(
    private oAuthService: OAuthService,
    private Response_Service: Response_Service,
    private Call_Info_Service: Call_Info_Service,
    private Environment_Service: Environment_Service,
    private router: Router,
    private localStorage_Service: LocalStorage_Service,
    @Optional() @SkipSelf() parentModule?: OAuth_Service) {
    //    this.initialise_OAuth();
    if (parentModule) {
      throw new Error(
        'OAuth_Service is already loaded. Import it in the AppModule only');
    }
  }
  //private access_token: string = null;

  public user_info: tms_internal_user_info = new tms_internal_user_info();
  claim_info: v1_3_identity_claim_options = new v1_3_identity_claim_options();

  /* Shared by the guards and components, so the discovery document / code exchange only happens once (an authorization code can only be redeemed once) */
  private login_Promise: Promise<boolean> = null;

  /* Authorization code + PKCE, refresh tokens (offline_access scope) replace the implicit flow's silent refresh iframe */
  public configure_Code_Flow(authConfig: AuthConfig) {
    authConfig.responseType = 'code';
    authConfig.useSilentRefresh = false;
  }

  public initialise_OAuth() {
    if (this.login_Promise != null) {
      return;
    }

    var authConfig: AuthConfig = new AuthConfig();
    authConfig.issuer = this.Environment_Service.environment.identity_server + "/";
    authConfig.redirectUri = window.location.protocol + "//" + window.location.host;
    authConfig.clientId = this.Environment_Service.environment.identity_client_id;
    authConfig.scope = this.Environment_Service.environment.identity_scope;
    this.configure_Code_Flow(authConfig);

    // persist nonces and PKCE verifier in localStorage to avoid nonce/state mismatches across reloads
    (authConfig as any).saveNoncesInLocalStorage = true;

    try {
      this.oAuthService.configure(authConfig);
      this.oAuthService.setStorage(localStorage);
    } catch (e) {
      console.debug('[OAuth_Service] initialise_OAuth: could not set localStorage for oauth', e);
    }


    /* getToken() prefers the stored switch_certificate token, which is never refreshed. Drop it whenever a refresh brings a new token
       (the identity server issues it for the user's current finance centre), getToken() then stores the new one */
    this.oAuthService.events.subscribe(e => {
      if (e.type === 'token_received' || e.type === 'token_refreshed') {
        this.localStorage_Service.removeAccessToken();
      }
    });

    this.login_Promise = this.oAuthService.loadDiscoveryDocumentAndTryLogin()
      .catch(err => {
        /* Let the next call try again, e.g. the identity server was unreachable */
        this.login_Promise = null;
        throw err;
      });
  }

  private tryLogin(): Promise<boolean> {
    this.initialise_OAuth();
    return this.login_Promise;
  }

  public login() {
    this.oAuthService.initCodeFlow();
  }

  public logoff() {
    this.localStorage_Service.removeAccessToken();
    this.oAuthService.logOut();
  }

  /* /connect/userinfo rejected the token (401), logout. Returns true when it logged off */
  public logoffIfUnauthorized(err: any, caller: string): boolean {
    if (err?.status === 401) {
      console.debug('[OAuth_Service] ' + caller + ': 401 from /connect/userinfo, logging off');
      this.logoff();
      return true;
    }
    return false;
  }

  public getUser_Info<T>() {
    if (this.isLoggedIn() == true) {
      var token = this.getToken();
      console.log('get userinfo');
      return this.Response_Service.getResponseInfo<T>(this.Environment_Service.environment.identity_server + "/connect/userinfo", token).pipe(
        tap({
          error: err => { this.logoffIfUnauthorized(err, 'getUser_Info'); }
        })
      );
    }
  }

  public getLogin_Password(User_Name: string, Password: string) {
    this.oAuthService.tokenEndpoint = this.Environment_Service.environment.identity_server + "/connect/token";
    this.oAuthService.clientId = this.Environment_Service.environment.identity_client_id;
    this.oAuthService.dummyClientSecret = this.Environment_Service.environment.identity_client_secret;

    return this.oAuthService.fetchTokenUsingPasswordFlow(User_Name, Password);
  }

  public isLoggedIn(): boolean {
    return (this.oAuthService.getAccessToken() != null && this.Call_Info_Service.Currently_Logged_In() == true);
  }

  public setToken(token: string) {
    console.debug('[OAuth_Service] setToken, tokenLen=', (token || '').length, new Date().toISOString());
    //this.access_token = token;
    this.localStorage_Service.setAccessToken(token);
    const stored = this.getTokenFromStorage() || '';
    console.debug('[OAuth_Service] token stored, tokenLen=', stored.length, new Date().toISOString());
  }

  /**
   * Non-secret id of the current access token (its jti, or iat), passed to TMS2 as tkid so it can tell when
   * the token behind its session has changed. TMS2 asks for the token itself (Auth/Handshake.aspx -> postMessage).
   */
  public getTokenId(): string {
    const payload = this.getToken().split('.')[1];
    if (!payload) return '';
    try {
      const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return String(claims.jti ?? claims.iat ?? '');
    } catch {
      return '';
    }
  }

  public getTokenFromStorage() {
    return this.localStorage_Service.getAccessToken();
  }

  public getToken(): string {
    if (this.Call_Info_Service.Currently_Logged_In() == true) {
      const stored = this.getTokenFromStorage();
      if (stored != null && stored.length > 0) {
        return stored;
      }

      const accessToken = this.oAuthService.getAccessToken();
      if (accessToken != null && accessToken.length > 0) {
        this.localStorage_Service.setAccessToken(accessToken);
        return accessToken;
      }

      return stored || "";
    }
    return "";
  }

  public getError_Message(errorId: string): Observable<error_message> {
    return this.Response_Service.getResponse<error_message>(this.Environment_Service.environment.identity_server + "/api/v1/identity/error?errorId=" + errorId);
  }

  public getUser_Claims(identity_login: v1_3_identity_request): Observable<HttpResponse<v1_3_identity_claim_options>> {
    return this.Response_Service.post_Data<v1_3_identity_claim_options>(this.Environment_Service.environment.identity_server + "/api/v1.3/" + this.Environment_Service.environment.identity_server_app_id + "/identity/login/claims", identity_login);
  }

  public cacheUserInfo() {
    if (!this.isLoggedIn()) {
      return;
    }

    this.getUser_Info<tms_internal_user_info>()?.subscribe(
      User_Info => {
        console.log('User Info received: ' + User_Info.operator_id + '-' + User_Info.finance_centre_id);
        this.user_info = User_Info;
        var Identity_Request: v1_3_identity_request = new v1_3_identity_request();
        Identity_Request.user_name = User_Info.name;

        this.getUser_Claims(Identity_Request).subscribe(Claim_Response => {
          this.claim_info = Claim_Response.body;
          //this.currentUserFinanceCentreId = this.claim_info.scopes[0].controls[0].value;
          this.user_info.certificate = this.claim_info.scopes[0].controls[0].options.find(fc => fc.id == this.user_info?.finance_centre_id).name;
        });
      },
      err => {
        //issue with getting the userinfo, could be because of expired token, logout.
        //would really need a refreshtoken here?
        console.log(err);

        //logout
        this.user_info = null;
        this.localStorage_Service.removeAccessToken();
        this.logoff();

        //try to login
        this.login();
      }
    );
  }

  // Promise-based version used during app startup to ensure user info is loaded
  public cacheUserInfoPromise(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.isLoggedIn()) {
        resolve();
        return;
      }

      this.getUser_Info<tms_internal_user_info>().subscribe(
        User_Info => {
          this.user_info = User_Info;
          var Identity_Request: v1_3_identity_request = new v1_3_identity_request();
          Identity_Request.user_name = User_Info.name;

          this.getUser_Claims(Identity_Request).subscribe(Claim_Response => {
            this.claim_info = Claim_Response.body;
            try {
              this.user_info.certificate = this.claim_info.scopes[0].controls[0].options.find(fc => fc.id == this.user_info?.finance_centre_id).name;
            } catch (e) {
              console.debug('[OAuth_Service] cacheUserInfoPromise: claim parse error', e);
            }
            resolve();
          }, err => {
            console.debug('[OAuth_Service] cacheUserInfoPromise: getUser_Claims error', err);
            resolve();
          });
        },
        err => {
          console.debug('[OAuth_Service] cacheUserInfoPromise: getUser_Info error', err);
          // logout flow similar to cacheUserInfo
          this.user_info = null;
          this.localStorage_Service.removeAccessToken();
          this.logoff();
          resolve();
        }
      );
    });
  }

  // Initialization helper to be used with APP_INITIALIZER to block app bootstrap
  public initForAppStart(): Promise<void> {
    // Use a timeout safety-net so the app doesn't stay blocked on IdP/redirect issues.
    return new Promise<void>((resolve) => {
      let finished = false;
      const timeoutMs = 5000;
      const timeout = setTimeout(() => {
        if (!finished) {
          console.debug('[OAuth_Service] initForAppStart: timeout reached, resolving to avoid blocking bootstrap');
          finished = true;
          resolve();
        }
      }, timeoutMs);

      this.oAuthService.loadDiscoveryDocumentAndTryLogin().then(() => {
        if (finished) return;
        if (this.oAuthService.hasValidAccessToken()) {
          // attempt to load profile via oauth lib then cache additional info
          this.oAuthService.loadUserProfile().then(() => {
            this.cacheUserInfoPromise().then(() => {
              if (!finished) {
                finished = true;
                clearTimeout(timeout);
                resolve();
              }
            });
          }).catch(err => {
            console.debug('[OAuth_Service] initForAppStart: loadUserProfile error', err);
            if (this.logoffIfUnauthorized(err, 'initForAppStart')) {
              if (!finished) {
                finished = true;
                clearTimeout(timeout);
                resolve();
              }
              return;
            }
            this.cacheUserInfoPromise().then(() => {
              if (!finished) {
                finished = true;
                clearTimeout(timeout);
                resolve();
              }
            });
          });
        } else {
          if (!finished) {
            finished = true;
            clearTimeout(timeout);
            resolve();
          }
        }
      }).catch(err => {
        // If the IdP returns nonce/state validation errors, clear stored nonces and continue
        try {
          const msg = err && (err.message || JSON.stringify(err)).toString();
          console.debug('[OAuth_Service] initForAppStart: discovery/login error', msg);
          if (msg && msg.indexOf && (msg.indexOf('invalid_nonce') !== -1 || msg.indexOf('invalid_nonce_in_state') !== -1 || msg.indexOf('wrong state/nonce') !== -1)) {
            try {
              localStorage.removeItem('nonce');
              localStorage.removeItem('PKCI_verifier');
              this.localStorage_Service.removeAccessToken();
              console.debug('[OAuth_Service] initForAppStart: cleared nonce and PKCI from storage due to validation error');
            } catch (e) {
              console.debug('[OAuth_Service] initForAppStart: error clearing nonce/PKCI', e);
            }
          }
        } catch (e) {
          console.debug('[OAuth_Service] initForAppStart: unexpected error handling discovery/login failure', e);
        }

        if (!finished) {
          finished = true;
          clearTimeout(timeout);
          resolve();
        }
      });
    });
  }

  public Switch_Certificate(User_Name: string, Finance_Centre_ID: string): Observable<HttpResponse<v1_3_identity_switch_certificate_response>> {
    var Switch_Certificate_Request: v1_3_identity_switch_certificate_request = new v1_3_identity_switch_certificate_request();
    Switch_Certificate_Request.user_name = User_Name;
    Switch_Certificate_Request.finance_centre_id = +Finance_Centre_ID;

    return this.Response_Service.post_Data<v1_3_identity_switch_certificate_response>(this.Environment_Service.environment.identity_server + "/api/v1.3/" + this.Environment_Service.environment.identity_server_app_id + "/identity/login/switch_certificate", Switch_Certificate_Request);
  }

  public get_Logout() {
    var Post_Body: HttpParams = new HttpParams();
    Post_Body.set("token", this.oAuthService.getAccessToken());
    Post_Body.set("client_id", this.Environment_Service.environment.identity_client_id);
    Post_Body.set("client_secret", this.Environment_Service.environment.identity_client_secret);

    this.Response_Service.post_Data_URL_Encoded(this.Environment_Service.environment.identity_server + "/connect/revocation", Post_Body);

    this.localStorage_Service.removeAccessToken();

    this.router.navigate(["/account/login"]);
  }

  public loadAndAuthorize(): Promise<boolean> {
    return this.tryLogin()
      .then(() => {

        if (!this.oAuthService.hasValidAccessToken()) {
          this.login();
        }
        else {
          return this.oAuthService.loadUserProfile().then(() => {
            return this.oAuthService.hasValidAccessToken();
          }, err => {
            if (this.logoffIfUnauthorized(err, 'loadAndAuthorize')) {
              return false;
            }
            throw err;
          });
        }
      });
  }

  get isAuthorized() {
    return this.tryLogin().then(() => {
      return this.oAuthService.hasValidAccessToken();
    });
  }

  get identityClaims(): tms_internal_user_info {
    const claims = this.oAuthService.getIdentityClaims() || {};
    return Object.assign(new tms_internal_user_info(), claims);
  }

}
